import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { sendEmailVerification } from 'firebase/auth';
import { Mail, RefreshCw } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { auth } from '../firebaseConfig';
import Button from './ui/Button';

function isEmailVerified(user) {
  if (user.emailVerified) return true;
  const isGoogle = user.providerData?.some(p => p.providerId === 'google.com');
  return !!isGoogle;
}

function EmailVerificationGate() {
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);
  const [checking, setChecking] = useState(false);

  const handleResend = async () => {
    setSending(true);
    setError(null);
    setSent(false);
    try {
      await sendEmailVerification(auth.currentUser, {
        url: `${window.location.origin}/verify-email`,
        handleCodeInApp: true,
      });
      setSent(true);
    } catch (err) {
      if (err.code === 'auth/too-many-requests') {
        setError('Muitas tentativas. Aguarde alguns minutos.');
      } else {
        setError('Erro ao enviar email. Tente novamente.');
      }
    } finally {
      setSending(false);
    }
  };

  const handleCheckAgain = async () => {
    setChecking(true);
    try {
      await auth.currentUser.reload();
      if (auth.currentUser.emailVerified) {
        window.location.reload();
      } else {
        setError('Email ainda nao verificado.');
      }
    } catch {
      setError('Erro ao verificar. Tente novamente.');
    } finally {
      setChecking(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-lg max-w-md w-full p-8 space-y-6 border border-purple-100 relative overflow-hidden">
        <div className="absolute top-0 right-0 -translate-y-8 translate-x-8 w-64 h-64 bg-gradient-to-br from-purple-200 to-pink-200 rounded-full filter blur-3xl opacity-30 animate-pulse pointer-events-none"></div>

        <div className="text-center space-y-3">
          <div className="w-16 h-16 mx-auto bg-gradient-to-br from-purple-100 to-pink-100 rounded-full flex items-center justify-center">
            <Mail className="w-8 h-8 text-purple-600" />
          </div>
          <h2 className="text-xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
            Verifique seu e-mail
          </h2>
          <p className="text-gray-600 text-sm">
            Enviamos um link de verificacao para o seu e-mail.
            Por favor, clique no link para ativar sua conta.
          </p>
        </div>

        {error && (
          <div className="bg-red-50 text-red-500 p-3 rounded-lg text-sm text-center">
            {error}
          </div>
        )}

        {sent && !error && (
          <div className="bg-green-50 text-green-600 p-3 rounded-lg text-sm text-center">
            Email enviado! Verifique sua caixa de entrada.
          </div>
        )}

        <div className="space-y-3">
          <Button
            onClick={handleCheckAgain}
            loading={checking}
            loadingText="Verificando..."
            fullWidth
          >
            <RefreshCw className="w-4 h-4" />
            Ja verifiquei meu e-mail
          </Button>

          <Button
            variant="secondary"
            onClick={handleResend}
            loading={sending}
            loadingText="Enviando..."
            disabled={sent}
            fullWidth
          >
            Reenviar e-mail de verificacao
          </Button>
        </div>

        <div className="absolute bottom-0 left-0 translate-y-8 -translate-x-8 w-64 h-64 bg-gradient-to-br from-purple-200 to-pink-200 rounded-full filter blur-3xl opacity-30 animate-pulse pointer-events-none"></div>
      </div>
    </div>
  );
}

export function PrivateRoute({ children }) {
  const { user } = useAuth();

  if (!user) return <Navigate to="/login" />;
  if (!isEmailVerified(user)) return <EmailVerificationGate />;
  return children;
}